<template>
  <div class="container-fluid mt-4 cripto-mcp-page">
    <div class="d-flex align-items-start justify-content-between mb-4 flex-wrap gap-3">
      <div>
        <h2 class="mb-1"><i class="bi bi-robot me-2"></i>Cripto MCP</h2>
        <p class="text-muted mb-0 small">
          Kraken en vivo, Trezor on-chain (BTC/ETH) y agente conversacional. Convive con
          <router-link to="/fi-cripto">FI y Cripto</router-link> (Google Sheets) hasta validar la integración.
        </p>
      </div>
      <div class="d-flex gap-2 flex-wrap">
        <router-link to="/fi-cripto" class="btn btn-outline-secondary btn-sm">
          <i class="bi bi-coin me-1"></i>FI y Cripto (sheet)
        </router-link>
        <router-link to="/perfil" class="btn btn-outline-primary btn-sm">
          <i class="bi bi-key me-1"></i>Credenciales
        </router-link>
      </div>
    </div>

    <div v-if="!status?.configured" class="alert alert-warning">
      <i class="bi bi-key me-2"></i>
      Configura tu API Key de Kraken en
      <router-link to="/perfil">Editar perfil</router-link>
      (solo lectura recomendada: Query Funds, Query Trades).
    </div>

    <ul class="nav nav-tabs mb-4" role="tablist">
      <li class="nav-item" role="presentation">
        <button class="nav-link" :class="{ active: tab === 'resumen' }" @click="tab = 'resumen'">
          <i class="bi bi-grid me-1"></i>Resumen
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button class="nav-link" :class="{ active: tab === 'cartera' }" @click="tab = 'cartera'; cargarPortfolio(true)">
          <i class="bi bi-wallet2 me-1"></i>Cartera Kraken
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button class="nav-link" :class="{ active: tab === 'trezor' }" @click="tab = 'trezor'; cargarOnchainPortfolio(true)">
          <i class="bi bi-shield-lock me-1"></i>Trezor
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button class="nav-link" :class="{ active: tab === 'staking' }" @click="tab = 'staking'; cargarStaking(true)">
          <i class="bi bi-piggy-bank me-1"></i>Staking
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button class="nav-link" :class="{ active: tab === 'agente' }" @click="tab = 'agente'; cargarHistorial()">
          <i class="bi bi-chat-dots me-1"></i>Agente
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button class="nav-link" :class="{ active: tab === 'mcp' }" @click="tab = 'mcp'; cargarMcpSetup()">
          <i class="bi bi-plug me-1"></i>MCP (Cursor)
        </button>
      </li>
    </ul>

    <div v-if="error" class="alert alert-danger">{{ error }}</div>

    <!-- Fase 4: Resumen -->
    <div v-show="tab === 'resumen'">
      <div v-if="cargandoResumen" class="text-center py-5">
        <div class="spinner-border text-primary"></div>
      </div>
      <template v-else>
        <div class="row g-3 mb-4">
          <div class="col-md-6 col-lg-3">
            <div class="card border-0 shadow-sm h-100 cripto-kpi-card">
              <div class="card-body">
                <div class="small text-muted text-uppercase mb-1">Kraken (en vivo)</div>
                <div class="fs-3 fw-bold">{{ formatoEuros(summary?.kraken?.totalEur) }}</div>
                <div class="small text-muted mt-1">
                  {{ summary?.kraken?.pricedCount ?? 0 }}/{{ summary?.kraken?.assetCount ?? 0 }} con precio €
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-6 col-lg-3">
            <div class="card border-0 shadow-sm h-100 cripto-kpi-trezor">
              <div class="card-body">
                <div class="small text-muted text-uppercase mb-1">Trezor (on-chain)</div>
                <div class="fs-3 fw-bold">{{ formatoEuros(onchainPortfolio?.totalEur) }}</div>
                <div class="small text-muted mt-1">
                  <template v-if="onchainStatus?.configured">
                    {{ onchainStatus.btcCount || 0 }} xpub · {{ onchainStatus.ethCount || 0 }} ETH
                    <span v-if="onchainPortfolio?.pricedAt"> · {{ formatPrecioHora(onchainPortfolio.pricedAt) }}</span>
                  </template>
                  <template v-else>
                    <router-link to="/perfil">Configurar en perfil</router-link>
                  </template>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-6 col-lg-3">
            <div class="card border-0 shadow-sm h-100 bg-dark text-white">
              <div class="card-body">
                <div class="small opacity-75 text-uppercase mb-1">Total cripto</div>
                <div class="fs-3 fw-bold">{{ formatoEuros(totalCriptoEur) }}</div>
                <div class="small opacity-75 mt-1">Kraken + Trezor (precios Kraken)</div>
              </div>
            </div>
          </div>
          <div class="col-md-6 col-lg-3">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body">
                <div class="small text-muted text-uppercase mb-1">Agente</div>
                <div class="fw-semibold">
                  Modo {{ summary?.agentMode === 'openai' ? 'IA (OpenAI)' : 'reglas' }}
                </div>
                <div class="small text-muted mt-1">
                  {{ status?.llmConfigured ? 'OPENAI_API_KEY en servidor' : 'Sin LLM — patrones' }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="card border-0 bg-light mb-3">
          <div class="card-body py-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
            <span class="small text-muted">Sheet (FI y Cripto) sigue disponible para comparar totales agregados.</span>
            <router-link to="/fi-cripto" class="btn btn-sm btn-outline-secondary">Abrir FI y Cripto</router-link>
          </div>
        </div>
        <p v-if="summary?.note" class="small text-muted">{{ summary.note }}</p>
      </template>
    </div>

    <!-- Fase 2: Cartera Kraken -->
    <div v-show="tab === 'cartera'">
      <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h3 class="h5 mb-0">Posiciones Kraken</h3>
          <p v-if="portfolio?.pricedAt" class="small text-muted mb-0">
            Precios Kraken · actualizado {{ formatPrecioHora(portfolio.pricedAt) }}
            <span v-if="autoRefreshActivo" class="ms-1">(auto cada {{ refreshSegundos }}s)</span>
          </p>
        </div>
        <button class="btn btn-outline-primary btn-sm" :disabled="cargandoPortfolio || !status?.configured" @click="cargarPortfolio(true)">
          <span v-if="cargandoPortfolio" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-arrow-clockwise me-1"></i>Actualizar
        </button>
      </div>
      <div v-if="cargandoPortfolio" class="text-center py-4">
        <div class="spinner-border text-primary"></div>
      </div>
      <template v-else-if="portfolio?.assets?.length">
        <div class="card border-0 shadow-sm mb-3 bg-dark text-white">
          <div class="card-body py-3">
            <div class="small opacity-75">Total estimado (EUR)</div>
            <div class="display-6 fw-bold">{{ formatoEurosTotal(portfolio.totalEur) }}</div>
            <div class="small opacity-75">
              {{ portfolio.pricedCount }}/{{ portfolio.assetCount }} activos con precio en €
              <span v-if="portfolio.pricedCount < portfolio.assetCount" class="opacity-75"> · sin par EUR/USD en Kraken</span>
            </div>
          </div>
        </div>
        <div class="table-responsive">
          <table class="table table-sm table-hover">
            <thead>
              <tr>
                <th>Activo</th>
                <th class="text-end">Cantidad</th>
                <th class="text-end">Precio €</th>
                <th class="text-end">Valor €</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="a in portfolio.assets" :key="a.asset">
                <td><span class="fw-semibold">{{ a.label }}</span> <span class="text-muted small">{{ a.asset }}</span></td>
                <td class="text-end">{{ fmtAmount(a.amount) }}</td>
                <td class="text-end">
                  <span>{{ fmtPrecioEur(a.priceEur) }}</span>
                  <span v-if="a.priceVia" class="text-muted small d-block">≈ {{ a.priceVia }}</span>
                  <span v-else-if="a.priceSource === 'usd'" class="text-muted small d-block">vía USD</span>
                </td>
                <td class="text-end fw-semibold">{{ a.valueEur != null ? formatoEurosValor(a.valueEur) : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
      <p v-else-if="!cargandoPortfolio" class="text-muted small">No hay posiciones o Kraken no está configurado.</p>
    </div>

    <!-- Trezor on-chain -->
    <div v-show="tab === 'trezor'">
      <div v-if="!onchainStatus?.configured" class="alert alert-warning">
        <i class="bi bi-shield-lock me-2"></i>
        Configura tu Trezor en modo <strong>solo lectura</strong> en
        <router-link to="/perfil">Editar perfil</router-link>:
        xpub de la cuenta BTC y dirección ETH (no se guardan claves privadas).
      </div>
      <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h3 class="h5 mb-0">Trezor — BTC y ETH on-chain</h3>
          <p v-if="onchainPortfolio?.pricedAt" class="small text-muted mb-0">
            Blockchain + precios Kraken · {{ formatPrecioHora(onchainPortfolio.pricedAt) }}
            <span v-if="autoRefreshActivo && tab === 'trezor'" class="ms-1">(auto cada {{ refreshSegundos }}s)</span>
          </p>
        </div>
        <button
          class="btn btn-outline-primary btn-sm"
          :disabled="cargandoOnchain || !onchainStatus?.configured"
          @click="cargarOnchainPortfolio(true)"
        >
          <span v-if="cargandoOnchain" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-arrow-clockwise me-1"></i>Actualizar
        </button>
      </div>
      <div v-if="cargandoOnchain" class="text-center py-4">
        <div class="spinner-border text-primary"></div>
      </div>
      <template v-else-if="onchainPortfolio">
        <div v-if="onchainPortfolio.errors?.length" class="alert alert-warning small py-2">
          <div v-for="err in onchainPortfolio.errors" :key="err.id">
            <strong>{{ err.asset }}</strong>
            <span v-if="err.source"> ({{ err.source }})</span>: {{ err.error }}
          </div>
        </div>
        <template v-if="onchainPortfolio.assets?.length">
          <div class="card border-0 shadow-sm mb-3 bg-dark text-white">
            <div class="card-body py-3">
              <div class="small opacity-75">Total estimado (EUR)</div>
              <div class="display-6 fw-bold">{{ formatoEurosTotal(onchainPortfolio.totalEur) }}</div>
              <div class="small opacity-75">
                {{ onchainPortfolio.note }}
                <span v-if="onchainPortfolio.btcTotal > 0" class="ms-2">· BTC Σ {{ fmtAmount(onchainPortfolio.btcTotal) }}</span>
                <span v-if="onchainPortfolio.ethTotal > 0" class="ms-2">· ETH Σ {{ fmtAmount(onchainPortfolio.ethTotal) }}</span>
              </div>
            </div>
          </div>
          <div class="table-responsive">
            <table class="table table-sm table-hover">
              <thead>
                <tr>
                  <th>Activo</th>
                  <th class="text-end">Cantidad</th>
                  <th class="text-end">Precio €</th>
                  <th class="text-end">Valor €</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="a in onchainPortfolio.assets" :key="a.id">
                  <td>
                    <span class="fw-semibold">{{ a.label }}</span>
                    <span class="text-muted small d-block">{{ a.asset }} · {{ a.sourcePreview || 'Trezor' }}</span>
                  </td>
                  <td class="text-end">{{ fmtAmount(a.amount) }}</td>
                  <td class="text-end">{{ fmtPrecioEur(a.priceEur) }}</td>
                  <td class="text-end fw-semibold">{{ a.valueEur != null ? formatoEurosValor(a.valueEur) : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
        <p v-else class="text-muted small mb-0">
          No se pudo cargar ningún saldo. Revisa las xpub/direcciones en tu perfil (formato zpub/xpub completo o 0x…).
        </p>
      </template>
    </div>

    <!-- Staking / Earn -->
    <div v-show="tab === 'staking'">
      <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h3 class="h5 mb-0">Staking y Earn</h3>
          <p v-if="staking?.pricedAt" class="small text-muted mb-0">
            Posiciones activas · {{ formatPrecioHora(staking.pricedAt) }}
          </p>
        </div>
        <button
          class="btn btn-outline-primary btn-sm"
          :disabled="cargandoStaking || !status?.configured"
          @click="cargarStaking(true)"
        >
          <span v-if="cargandoStaking" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-arrow-clockwise me-1"></i>Actualizar
        </button>
      </div>

      <div v-if="cargandoStaking" class="text-center py-4">
        <div class="spinner-border text-primary"></div>
      </div>
      <template v-else-if="staking?.positions?.length">
        <div class="card border-0 shadow-sm mb-3 bg-dark text-white">
          <div class="card-body py-3">
            <div class="row g-3">
              <div class="col-md-6">
                <div class="small opacity-75">Recompensa estimada (mensual)</div>
                <div class="fs-4 fw-bold">{{ formatoEurosValor(staking.totalEstimatedMonthlyEur) }}</div>
                <div class="small opacity-75">Suma APR medio × cantidad en staking</div>
              </div>
              <div class="col-md-6">
                <div class="small opacity-75">Posiciones activas</div>
                <div class="fs-4 fw-bold">{{ staking.positionCount }}</div>
              </div>
            </div>
          </div>
        </div>
        <p v-if="staking.note" class="small text-muted">{{ staking.note }}</p>
        <div class="table-responsive">
          <table class="table table-sm table-hover align-middle">
            <thead>
              <tr>
                <th>Producto</th>
                <th class="text-end">Cantidad</th>
                <th class="text-end">APR est.</th>
                <th class="text-end">Est. / mes €</th>
                <th class="text-end">Acumulado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in staking.positions" :key="p.id">
                <td>
                  <span class="fw-semibold">{{ p.label }}</span>
                  <span class="text-muted small d-block">{{ p.lockType || 'Earn' }}</span>
                  <span v-if="p.payoutLabel" class="text-muted small">Pago: {{ p.payoutLabel }}</span>
                </td>
                <td class="text-end">{{ fmtAmount(p.amount) }}</td>
                <td class="text-end">
                  <span v-if="p.aprLow != null && p.aprHigh != null">
                    {{ fmtApr(p.aprLow) }} – {{ fmtApr(p.aprHigh) }}
                  </span>
                  <span v-else-if="p.aprPercent != null">{{ fmtApr(p.aprPercent) }}</span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td class="text-end fw-semibold">
                  {{ p.estimatedMonthlyEur != null ? formatoEurosValor(p.estimatedMonthlyEur) : '—' }}
                </td>
                <td class="text-end">
                  <span v-if="p.totalRewardedNative != null && p.totalRewardedNative > 0">
                    {{ fmtAmount(p.totalRewardedNative) }}
                    <span v-if="p.totalRewardedEur != null" class="text-muted small d-block">
                      {{ formatoEurosValor(p.totalRewardedEur) }}
                    </span>
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td class="text-end">
                  <button
                    type="button"
                    class="btn btn-outline-secondary btn-sm"
                    :disabled="cargandoRecompensas"
                    @click="abrirRecompensas(p)"
                  >
                    <i class="bi bi-clock-history me-1"></i>Recompensas
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
      <p v-else-if="!cargandoStaking && status?.configured" class="text-muted small">
        No hay posiciones en staking o earn activas en Kraken.
      </p>
      <p v-else-if="!cargandoStaking" class="text-muted small">Configura Kraken para ver staking.</p>
    </div>

    <!-- Modal recompensas -->
    <div
      v-if="rewardsModal.open"
      class="modal fade show d-block cripto-rewards-backdrop"
      tabindex="-1"
      @click.self="cerrarRecompensas"
    >
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Recompensas — {{ rewardsModal.position?.label }}
            </h5>
            <button type="button" class="btn-close" @click="cerrarRecompensas"></button>
          </div>
          <div class="modal-body">
            <div v-if="cargandoRecompensas" class="text-center py-4">
              <div class="spinner-border text-primary"></div>
            </div>
            <template v-else-if="rewardsModal.data">
              <div v-if="rewardsModal.data.ledgerUnavailable" class="alert alert-warning small">
                {{ rewardsModal.data.ledgerMessage }}
              </div>
              <div v-if="rewardsModal.position?.totalRewardedNative > 0" class="mb-3 small">
                <strong>Total acumulado (Kraken Allocations):</strong>
                {{ fmtAmount(rewardsModal.position.totalRewardedNative) }}
                <span v-if="rewardsModal.position.totalRewardedEur != null">
                  ({{ formatoEurosValor(rewardsModal.position.totalRewardedEur) }})
                </span>
              </div>
              <p v-if="!rewardsModal.data.rewards?.length && !rewardsModal.data.ledgerUnavailable" class="text-muted small mb-0">
                No hay abonos de tipo «staking» en el ledger para este activo, o el vault auto-compone sin líneas individuales.
              </p>
              <div v-else-if="rewardsModal.data.rewards?.length" class="table-responsive">
                <table class="table table-sm table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Activo</th>
                      <th class="text-end">Cantidad</th>
                      <th class="text-end">Saldo tras abono</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in rewardsModal.data.rewards" :key="r.id">
                      <td class="text-nowrap">{{ formatLedgerTime(r.time) }}</td>
                      <td><span class="small">{{ r.asset }}</span></td>
                      <td class="text-end text-success fw-semibold">+{{ fmtAmount(r.amount) }}</td>
                      <td class="text-end text-muted">{{ fmtAmount(r.balance) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary btn-sm" @click="cerrarRecompensas">Cerrar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Fase 3: Agente -->
    <div v-show="tab === 'agente'">
      <div class="card border-0 shadow-sm">
        <div class="card-header bg-white d-flex justify-content-between align-items-center py-2">
          <span class="fw-semibold"><i class="bi bi-chat-left-text me-1"></i>Asistente Cripto</span>
          <button class="btn btn-link btn-sm text-danger" @click="borrarHistorial" :disabled="!chatMessages.length">
            Borrar historial
          </button>
        </div>
        <div ref="chatContainer" class="card-body cripto-chat-messages">
          <div v-if="!chatMessages.length" class="text-muted small text-center py-4">
            Pregunta sobre tu cartera Kraken, precios o operaciones recientes.
          </div>
          <div
            v-for="(msg, i) in chatMessages"
            :key="i"
            class="mb-3"
            :class="msg.role === 'user' ? 'text-end' : ''"
          >
            <div
              class="d-inline-block px-3 py-2 rounded-3 small text-start cripto-chat-bubble"
              :class="msg.role === 'user' ? 'bg-primary text-white' : 'bg-light'"
            >
              <div class="fw-semibold mb-1" style="font-size: 0.7rem; opacity: 0.85;">
                {{ msg.role === 'user' ? 'Tú' : 'Agente' }}
              </div>
              <div class="cripto-chat-content" v-html="formatChat(msg.content)"></div>
            </div>
          </div>
          <div v-if="enviandoChat" class="text-muted small"><span class="spinner-border spinner-border-sm me-1"></span>Pensando…</div>
        </div>
        <div class="card-footer bg-white">
          <form @submit.prevent="enviarMensaje" class="d-flex gap-2">
            <input
              v-model="chatInput"
              type="text"
              class="form-control form-control-sm"
              placeholder="Ej: ¿Cuál es mi cartera en Kraken?"
              :disabled="enviandoChat || !status?.configured"
              maxlength="4000"
            />
            <button type="submit" class="btn btn-primary btn-sm" :disabled="enviandoChat || !chatInput.trim() || !status?.configured">
              Enviar
            </button>
          </form>
          <div class="d-flex flex-wrap gap-1 mt-2">
            <button
              v-for="s in sugerencias"
              :key="s"
              type="button"
              class="btn btn-outline-secondary btn-sm"
              style="font-size: 0.75rem;"
              @click="chatInput = s"
            >{{ s }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Fase 1: MCP Cursor -->
    <div v-show="tab === 'mcp'">
      <div v-if="cargandoMcp" class="text-center py-4">
        <div class="spinner-border text-primary"></div>
      </div>
      <template v-else-if="mcpSetup">
        <p class="text-muted small">
          {{ mcpSetup.title }} — para desarrollar y probar con Cursor mientras la app usa la API REST en el backend.
        </p>
        <div v-for="opt in mcpSetup.options" :key="opt.id" class="card border-0 shadow-sm mb-3">
          <div class="card-header bg-white fw-semibold">{{ opt.name }}</div>
          <div class="card-body">
            <p class="small mb-2">
              <a :href="opt.docs" target="_blank" rel="noopener">Documentación</a>
            </p>
            <pre class="bg-dark text-light p-3 rounded small mb-0 overflow-auto"><code>{{ JSON.stringify(opt.config, null, 2) }}</code></pre>
          </div>
        </div>
        <div class="alert alert-info small mb-0">
          Copia la configuración en Cursor → Settings → MCP, o en <code>.cursor/mcp.json</code> del proyecto.
          La app en producción no expone MCP al navegador; usa <code>/api/kraken/*</code>.
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from '../config/axios';
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';

const REFRESH_MS = 30000;

export default {
  name: 'CriptoMcp',
  setup() {
    const tab = ref('resumen');
    const error = ref('');
    const status = ref(null);
    const summary = ref(null);
    const portfolio = ref(null);
    const cargandoResumen = ref(false);
    const cargandoPortfolio = ref(false);
    const staking = ref(null);
    const cargandoStaking = ref(false);
    const cargandoRecompensas = ref(false);
    const rewardsModal = ref({ open: false, position: null, data: null });
    const mcpSetup = ref(null);
    const cargandoMcp = ref(false);
    const onchainStatus = ref(null);
    const onchainPortfolio = ref(null);
    const cargandoOnchain = ref(false);
    const chatMessages = ref([]);
    const chatInput = ref('');
    const enviandoChat = ref(false);
    const chatContainer = ref(null);
    const autoRefreshActivo = ref(false);
    const refreshSegundos = REFRESH_MS / 1000;
    let refreshTimer = null;

    const totalCriptoEur = computed(() => {
      const k = summary.value?.kraken?.totalEur;
      const t = onchainPortfolio.value?.totalEur;
      const parts = [k, t].filter((n) => n != null && Number.isFinite(n));
      if (!parts.length) return null;
      return parts.reduce((s, n) => s + n, 0);
    });

    const sugerencias = [
      '¿Cuál es mi cartera?',
      'Precio BTC y ETH',
      'Últimas operaciones',
      'Órdenes abiertas',
    ];

    function formatoEuros(n) {
      if (n == null || !Number.isFinite(n)) return '—';
      return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
    }

    function formatoEurosTotal(n) {
      if (n == null || !Number.isFinite(n)) return '—';
      return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: n >= 1000 ? 0 : 2,
      }).format(n);
    }

    function formatoEurosValor(n) {
      if (n == null || !Number.isFinite(n)) return '—';
      return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n);
    }

    function fmtPrecioEur(n) {
      if (n == null || !Number.isFinite(n)) return '—';
      const abs = Math.abs(n);
      let max = 8;
      if (abs >= 1000) max = 2;
      else if (abs >= 1) max = 4;
      else if (abs >= 0.01) max = 6;
      return `${n.toLocaleString('es-ES', { maximumFractionDigits: max })} €`;
    }

    function formatPrecioHora(iso) {
      if (!iso) return '';
      return new Date(iso).toLocaleString('es-ES', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    }

    function fmtAmount(n) {
      if (n == null || !Number.isFinite(n)) return '—';
      if (Math.abs(n) >= 1) return n.toLocaleString('es-ES', { maximumFractionDigits: 6 });
      return n.toLocaleString('es-ES', { maximumFractionDigits: 8 });
    }

    function fmtApr(n) {
      if (n == null || !Number.isFinite(n)) return '—';
      return `${n.toLocaleString('es-ES', { maximumFractionDigits: 2 })} %`;
    }

    function formatLedgerTime(iso) {
      if (!iso) return '—';
      return new Date(iso).toLocaleString('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    async function cargarStaking(manual = false) {
      if (!status.value?.configured) return;
      if (manual) cargandoStaking.value = true;
      try {
        const { data } = await axios.get('/api/kraken/staking');
        staking.value = data;
        if (manual) error.value = '';
      } catch (e) {
        if (manual || !staking.value) {
          error.value = e.response?.data?.error || 'Error al cargar staking';
        }
      } finally {
        if (manual) cargandoStaking.value = false;
      }
    }

    async function abrirRecompensas(position) {
      rewardsModal.value = { open: true, position, data: null };
      cargandoRecompensas.value = true;
      try {
        const params = {
          strategy_id: position.strategyId || undefined,
          asset_code: position.assetCode || position.asset,
          asset: position.nativeSymbol || undefined,
        };
        const { data } = await axios.get('/api/kraken/staking/rewards', { params });
        rewardsModal.value = { open: true, position, data };
      } catch (e) {
        rewardsModal.value = {
          open: true,
          position,
          data: {
            rewards: [],
            ledgerUnavailable: true,
            ledgerMessage: e.response?.data?.error || 'Error al cargar recompensas',
          },
        };
      } finally {
        cargandoRecompensas.value = false;
      }
    }

    function cerrarRecompensas() {
      rewardsModal.value = { open: false, position: null, data: null };
    }

    function formatChat(text) {
      if (!text) return '';
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\n/g, '<br>');
    }

    async function scrollChat() {
      await nextTick();
      const el = chatContainer.value;
      if (el) el.scrollTop = el.scrollHeight;
    }

    async function cargarStatus() {
      try {
        const { data } = await axios.get('/api/kraken/status');
        status.value = data;
      } catch (e) {
        error.value = e.response?.data?.error || 'Error al cargar estado';
      }
    }

    async function cargarOnchainStatus() {
      try {
        const { data } = await axios.get('/api/onchain/status');
        onchainStatus.value = data;
      } catch {
        onchainStatus.value = { configured: false };
      }
    }

    async function cargarOnchainPortfolio(manual = false) {
      if (!onchainStatus.value?.configured) return;
      if (manual) cargandoOnchain.value = true;
      try {
        const { data } = await axios.get('/api/onchain/portfolio');
        onchainPortfolio.value = data;
        if (manual) error.value = '';
      } catch (e) {
        if (manual || !onchainPortfolio.value) {
          error.value = e.response?.data?.error || 'Error al cargar Trezor';
        }
      } finally {
        if (manual) cargandoOnchain.value = false;
      }
    }

    async function cargarResumen() {
      cargandoResumen.value = true;
      error.value = '';
      try {
        const { data } = await axios.get('/api/kraken/summary');
        summary.value = data;
        if (onchainStatus.value?.configured) {
          await cargarOnchainPortfolio(false);
        }
      } catch (e) {
        error.value = e.response?.data?.error || 'Error al cargar resumen';
      } finally {
        cargandoResumen.value = false;
      }
    }

    async function cargarPortfolio(manual = false) {
      if (!status.value?.configured) return;
      if (manual) cargandoPortfolio.value = true;
      try {
        const { data } = await axios.get('/api/kraken/portfolio');
        portfolio.value = data;
        if (summary.value?.kraken) {
          summary.value.kraken = {
            totalEur: data.totalEur,
            assetCount: data.assetCount,
            pricedCount: data.pricedCount,
          };
          summary.value.pricedAt = data.pricedAt;
        }
        if (manual) error.value = '';
      } catch (e) {
        if (manual || !portfolio.value) {
          error.value = e.response?.data?.error || 'Error al cargar cartera';
        }
      } finally {
        if (manual) cargandoPortfolio.value = false;
      }
    }

    function iniciarAutoRefresh() {
      detenerAutoRefresh();
      const canRefresh =
        status.value?.configured ||
        onchainStatus.value?.configured;
      if (!canRefresh) return;
      autoRefreshActivo.value = true;
      refreshTimer = setInterval(() => {
        if (tab.value === 'cartera') cargarPortfolio(false);
        else if (tab.value === 'trezor') cargarOnchainPortfolio(false);
        else if (tab.value === 'resumen') cargarResumenSilencioso();
      }, REFRESH_MS);
    }

    function detenerAutoRefresh() {
      autoRefreshActivo.value = false;
      if (refreshTimer) {
        clearInterval(refreshTimer);
        refreshTimer = null;
      }
    }

    async function cargarResumenSilencioso() {
      try {
        const { data } = await axios.get('/api/kraken/summary');
        summary.value = data;
        if (onchainStatus.value?.configured) {
          await cargarOnchainPortfolio(false);
        }
      } catch {
        /* ignorar en polling */
      }
    }

    async function cargarHistorial() {
      try {
        const { data } = await axios.get('/api/kraken/chat/history');
        chatMessages.value = data.messages || [];
        await scrollChat();
      } catch {
        chatMessages.value = [];
      }
    }

    async function cargarMcpSetup() {
      if (mcpSetup.value) return;
      cargandoMcp.value = true;
      try {
        const { data } = await axios.get('/api/kraken/mcp-setup');
        mcpSetup.value = data;
      } catch (e) {
        error.value = e.response?.data?.error || 'Error al cargar setup MCP';
      } finally {
        cargandoMcp.value = false;
      }
    }

    async function enviarMensaje() {
      const text = chatInput.value.trim();
      if (!text || enviandoChat.value) return;
      enviandoChat.value = true;
      error.value = '';
      chatMessages.value.push({ role: 'user', content: text });
      chatInput.value = '';
      await scrollChat();
      try {
        const { data } = await axios.post('/api/kraken/agent', { message: text });
        chatMessages.value.push({ role: 'assistant', content: data.reply });
      } catch (e) {
        error.value = e.response?.data?.error || 'Error del agente';
        chatMessages.value.pop();
        chatInput.value = text;
      } finally {
        enviandoChat.value = false;
        await scrollChat();
      }
    }

    async function borrarHistorial() {
      if (!window.confirm('¿Borrar el historial del chat?')) return;
      try {
        await axios.delete('/api/kraken/chat/history');
        chatMessages.value = [];
      } catch (e) {
        error.value = e.response?.data?.error || 'Error al borrar historial';
      }
    }

    watch(tab, (t) => {
      if (t === 'cartera' && status.value?.configured && !portfolio.value) {
        cargarPortfolio(true);
      }
      if (t === 'trezor' && onchainStatus.value?.configured && !onchainPortfolio.value) {
        cargarOnchainPortfolio(true);
      }
      if (t === 'staking' && status.value?.configured && !staking.value) {
        cargarStaking(true);
      }
      const needsRefresh =
        (t === 'cartera' && status.value?.configured) ||
        (t === 'trezor' && onchainStatus.value?.configured) ||
        (t === 'resumen' && (status.value?.configured || onchainStatus.value?.configured));
      if (needsRefresh) iniciarAutoRefresh();
      else if (t !== 'cartera' && t !== 'trezor' && t !== 'resumen') detenerAutoRefresh();
    });

    onMounted(async () => {
      await Promise.all([cargarStatus(), cargarOnchainStatus()]);
      await cargarResumen();
      iniciarAutoRefresh();
      if (tab.value === 'cartera' && status.value?.configured) await cargarPortfolio(true);
      if (tab.value === 'trezor' && onchainStatus.value?.configured) await cargarOnchainPortfolio(true);
    });

    onUnmounted(() => {
      detenerAutoRefresh();
    });

    return {
      tab,
      error,
      status,
      summary,
      portfolio,
      cargandoResumen,
      cargandoPortfolio,
      staking,
      cargandoStaking,
      cargandoRecompensas,
      rewardsModal,
      mcpSetup,
      cargandoMcp,
      onchainStatus,
      onchainPortfolio,
      cargandoOnchain,
      totalCriptoEur,
      cargarOnchainPortfolio,
      chatMessages,
      chatInput,
      enviandoChat,
      chatContainer,
      sugerencias,
      autoRefreshActivo,
      refreshSegundos,
      formatoEuros,
      formatoEurosTotal,
      formatoEurosValor,
      fmtPrecioEur,
      formatPrecioHora,
      fmtAmount,
      fmtApr,
      formatLedgerTime,
      formatChat,
      cargarPortfolio,
      cargarStaking,
      abrirRecompensas,
      cerrarRecompensas,
      cargarHistorial,
      cargarMcpSetup,
      enviarMensaje,
      borrarHistorial,
    };
  },
};
</script>

<style scoped>
.cripto-mcp-page {
  max-width: 960px;
}

.cripto-kpi-card {
  border-left: 4px solid #f7931a !important;
}

.cripto-kpi-trezor {
  border-left: 4px solid #5cdb95 !important;
}

.cripto-chat-messages {
  max-height: 420px;
  overflow-y: auto;
}

.cripto-chat-bubble {
  max-width: 90%;
}

.cripto-chat-content :deep(strong) {
  font-weight: 600;
}

.cripto-rewards-backdrop {
  background: rgba(0, 0, 0, 0.45);
}
</style>
